import { type ReactNode, useId, useRef, useState } from 'react';

import { cx } from 'cva';
import { FieldErrorContext } from 'react-aria-components/FieldError';
import { Group } from 'react-aria-components/Group';

import { Button } from '../button/button';
import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';
import { Icon } from '../icon/icon';
import { TextField } from '../text_field/text_field';

import styles from './array_text_field.module.css';

export type ArrayTextFieldProps = {
  /** The list of values. Order is preserved and duplicates are allowed. */
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  description?: ReactNode;
  /** Error message for the list as a whole, e.g. "at least one value is required". */
  errorMessage?: string;
  /** Error messages for individual values, aligned by index with `value`. */
  itemErrorMessages?: (string | undefined)[];
  placeholder?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  size?: 's' | 'm' | 'l';
  /** maxLength for value, applied to each individual text input. */
  maxLength?: number;
  name?: string;
  className?: string;
  /** Called when focus leaves the field entirely, not when moving between rows. */
  onBlur?: () => void;
  addInputAriaLabel?: string;
  addButtonAriaLabel?: string;
  /** Accessible name for the button that removes an existing value. */
  getRemoveButtonAriaLabel?: (value: string, index: number) => string;
};

/**
 * Field for editing a list of strings, rendered as one text input per value
 * with a trailing input for adding the next one.
 *
 * Prefer `TagInputField` when values are short, unordered labels. Use
 * `ArrayTextField` when order matters, duplicates are meaningful, or values may
 * contain commas, such as glob and regular expression patterns.
 *
 * Every value that is typed is captured immediately: the trailing input's text
 * becomes the last value as it is entered, so nothing needs to be "committed".
 * Enter or the add button start a fresh entry; blurring the field never adds or
 * changes a value. Whitespace-only input is ignored. Consumers should drop empty
 * strings from the submitted value, since a row can be cleared without removing it.
 *
 * ```tsx
 * <ArrayTextField
 *   label="Ignore patterns"
 *   value={patterns}
 *   onChange={setPatterns}
 * />
 * ```
 */
export function ArrayTextField({
  addButtonAriaLabel,
  addInputAriaLabel,
  className,
  description,
  errorMessage,
  getRemoveButtonAriaLabel = (item, index) =>
    `Remove ${item || `value ${index + 1}`}`,
  isDisabled,
  isInvalid,
  isReadOnly,
  isRequired,
  itemErrorMessages,
  label,
  maxLength,
  name,
  onBlur,
  onChange,
  placeholder,
  size = 'm',
  value,
}: ArrayTextFieldProps) {
  const [draftActive, setDraftActive] = useState(false);
  const labelId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const rowRefs = useRef<(HTMLInputElement | null)[]>([]);
  const addRowRef = useRef<HTMLInputElement>(null);

  const showError = Boolean(isInvalid && errorMessage);

  const committedValues = draftActive ? value.slice(0, -1) : value;
  const draftValue = draftActive ? (value[value.length - 1] ?? '') : '';
  const hasDraft = draftValue.trim().length > 0;
  const draftErrorMessage = draftActive
    ? itemErrorMessages?.[value.length - 1]
    : undefined;

  // Rows are keyed by index, so removing the row at `index` doesn't unmount
  // it: React reuses that DOM node for the row that shifts up to fill it.
  // The last row has nothing to shift into it, so it unmounts instead, and
  // focus should land on the add input.
  const focusRowAfterRemoval = (index: number) => {
    const inputElement =
      index === committedValues.length - 1
        ? addRowRef.current
        : rowRefs.current[index];
    inputElement?.focus();
  };

  const setValueAtIndex = (index: number, nextValue: string) => {
    onChange(
      value.map((existing, itemIndex) =>
        itemIndex === index ? nextValue : existing,
      ),
    );
  };

  const handleDraftChange = (nextValue: string) => {
    const isBlank = nextValue.trim() === '';
    if (draftActive) {
      if (isBlank) {
        onChange(value.slice(0, -1));
        setDraftActive(false);
        return;
      }
      setValueAtIndex(value.length - 1, nextValue);
      return;
    }
    if (isBlank) {
      return;
    }

    onChange([...value, nextValue]);
    setDraftActive(true);
  };

  const startNewValue = () => {
    if (!hasDraft) {
      return;
    }
    setDraftActive(false);
    addRowRef.current?.focus();
  };

  const getItemAriaLabel = (index: number) =>
    label ? `${label} ${index + 1}` : `Value ${index + 1}`;

  const resolvedAddInputAriaLabel =
    addInputAriaLabel ?? (label ? `New ${label} value` : 'New value');
  const resolvedAddButtonAriaLabel =
    addButtonAriaLabel ?? (label ? `Add ${label}` : 'Add value');

  // Every input carries the field-level description and error. Putting these on
  // the Group alone would hide them from screen readers, as a role="group" is
  // not focusable and never announces its own description.
  const fieldDescribedBy =
    [description ? descriptionId : null, showError ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div
      className={cx(styles.array_text_field, className)}
      onBlur={(event) => {
        // Only report a blur once focus has left the field altogether, rather
        // than each time it moves between the rows.
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onBlur?.();
        }
      }}
    >
      {label && (
        <FieldLabel id={labelId} isRequired={isRequired}>
          {label}
        </FieldLabel>
      )}
      <Group
        className={styles.rows}
        isDisabled={isDisabled}
        isInvalid={showError}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={fieldDescribedBy}
      >
        {committedValues.map((item, index) => (
          <div key={index} className={styles.row}>
            <TextField
              aria-describedby={fieldDescribedBy}
              aria-label={getItemAriaLabel(index)}
              className={styles.input}
              errorMessage={itemErrorMessages?.[index]}
              inputRef={(element) => {
                rowRefs.current[index] = element;
              }}
              isDisabled={isDisabled}
              isInvalid={Boolean(itemErrorMessages?.[index])}
              isReadOnly={isReadOnly}
              isRequired={isRequired}
              maxLength={maxLength}
              name={name === undefined ? undefined : `${name}[${index}]`}
              size={size}
              validationBehavior="aria"
              value={item}
              onChange={(nextItem) => {
                setValueAtIndex(index, nextItem);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
            {!isReadOnly && (
              <Button
                aria-label={getRemoveButtonAriaLabel(item, index)}
                variant="tertiary"
                size={size}
                icon={<Icon name="close" size="s" color="primary_red" />}
                isDisabled={isDisabled}
                onPress={() => {
                  focusRowAfterRemoval(index);
                  onChange(value.filter((_, itemIndex) => itemIndex !== index));
                }}
              />
            )}
          </div>
        ))}
        {!isReadOnly && (
          <div className={styles.row}>
            <TextField
              aria-describedby={fieldDescribedBy}
              aria-label={resolvedAddInputAriaLabel}
              className={styles.input}
              errorMessage={draftErrorMessage}
              inputRef={addRowRef}
              isDisabled={isDisabled}
              isInvalid={Boolean(draftErrorMessage)}
              maxLength={maxLength}
              placeholder={placeholder}
              size={size}
              value={draftValue}
              onChange={handleDraftChange}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  startNewValue();
                }
              }}
            />
            <Button
              aria-label={resolvedAddButtonAriaLabel}
              variant="tertiary"
              size={size}
              icon={
                <Icon
                  name="plus"
                  size="s"
                  color={hasDraft ? 'primary_blue' : 'primary_neutral'}
                />
              }
              isDisabled={isDisabled || !hasDraft}
              onPress={startNewValue}
            />
          </div>
        )}
      </Group>
      {description && (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      )}
      {/*
       * This is a group of fields rather than a single React Aria field, so no
       * RAC wrapper supplies FieldErrorContext. Providing it by hand is what
       * lets `FieldError` render and be styled like every other UAD field.
       */}
      <FieldErrorContext.Provider
        value={{
          isInvalid: showError,
          validationErrors: errorMessage ? [errorMessage] : [],
          validationDetails: {
            badInput: false,
            customError: showError,
            patternMismatch: false,
            rangeOverflow: false,
            rangeUnderflow: false,
            stepMismatch: false,
            tooLong: false,
            tooShort: false,
            typeMismatch: false,
            valid: !showError,
            valueMissing: false,
          },
        }}
      >
        <FieldError id={errorId} />
      </FieldErrorContext.Provider>
    </div>
  );
}
